import { Component } from '@theme/component';

/**
 * A custom element that renders a video background.
 * Video sources are attached lazily when the element nears the viewport.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | null} */
  #observer = null;
  #loaded = false;

  connectedCallback() {
    super.connectedCallback();

    const saveData = /** @type {any} */ (navigator).connection?.saveData;
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (saveData || reducedMotion) return;

    if (!('IntersectionObserver' in window)) {
      this.#attach();
      this.#play();
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#attach();
            this.#play();
          } else if (this.#loaded) {
            this.refs.videoElement.pause();
          }
        }
      },
      { rootMargin: '200px' }
    );
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#observer?.disconnect();
    this.#observer = null;
  }

  #attach() {
    if (this.#loaded) return;
    this.#loaded = true;

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) source.setAttribute('src', videoSource);
    }

    videoElement.load();
  }

  #play() {
    const result = this.refs.videoElement.play();
    if (result && typeof result.catch === 'function') result.catch(() => {});
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
